import { FC, useRef, useState } from 'react';
import { Modal, Typography, Table, Badge, cn } from '@crisellevaldez/speculo-ui';
import { STAT_CELL_TEXT } from '../../../utils/statsStyles';
import { OffsetPagination } from '../../common/OffsetPagination';
import { ExpandableTextCell } from '../../common/ExpandableTextCell';
import { BackButton } from '../../common/BackButton';
import {
  Calendar,
  Phone,
  PhoneIncoming,
  PhoneOutgoing,
  Voicemail,
  XCircle,
  CheckCircle,
  ChevronDown,
  ChevronUp,
} from 'lucide-react';
import {
  GQLLiveTransferCall,
  useOrgLiveTransfersGQL,
} from '../../../hooks/graphql/dashboard/useOrgLiveTransfersGQL';
import { useOrganizationUsers } from '../../../hooks/graphql/users/useOrganizationUsers';
import { AssignedAgentCell } from '../../common/AssignedAgentCell';
import type { AssignedAgent } from '../../../types/contact.types';
import { ReactNode } from 'react';
import { formatDate } from '../../../utils/dateTime';
import { formatNumber } from '../../../utils/format';
import { formatPhoneNumber } from '../../../utils/formatters';
import { CallDetailsPanel } from './CallDetailsPanel';
import { CallStatsHeader } from './CallStatsHeader';
import { CallRecordingPlayer } from '../../common/CallRecordingPlayer';
import { formatTransferReason } from '../../../utils/transferReason';

/**
 * Derive a definitive answered state when the LiveTransfers.answered column
 * is NULL on Spanner. ~73% of prod transfers carry NULL answered because the
 * writers never set it for terminal-failure paths (timeout / voicemail / etc),
 * even though the status column makes the outcome unambiguous.
 *
 * Returns true / false for definitive states, or null when the transfer is
 * still in flight (pending/initiated/ringing) so the UI can still render an
 * honest "—".
 */
function deriveAnswered(answered: boolean | null | undefined, status: string | null | undefined): boolean | null {
  if (answered === true || answered === false) return answered;
  const s = (status || '').toLowerCase();
  if (!s) return null;
  if (s === 'accepted' || s === 'completed' || s === 'claimed' || s === 'answered') return true;
  if (
    s === 'failed' || s === 'expired' || s === 'cancelled' || s === 'canceled' ||
    s === 'declined' || s === 'no-answer' || s === 'voicemail' ||
    s.startsWith('voicemail-') || s.startsWith('timeout-')
  ) return false;
  return null;
}

/** Map a GQLLiveTransferCall → CallDetailsPanel's expected Call shape. */
// eslint-disable-next-line @typescript-eslint/no-explicit-any
function adaptTransferToCall(t: GQLLiveTransferCall): any {
  const tryParse = (v: string | null | undefined) => {
    if (!v) return undefined;
    try { return JSON.parse(v); } catch { return undefined; }
  };
  const timeline = (() => {
    const parsed = tryParse(t.callAnalysisTimeline);
    return Array.isArray(parsed) ? parsed : undefined;
  })();
  const answers = tryParse(t.callAnalysisAnswers);
  return {
    id: t.callId,
    call_id: t.callId,
    inbound: t.inbound ?? false,
    callTimestamp: t.initiatedAt ? new Date(t.initiatedAt) : undefined,
    duration: t.durationSeconds ?? 0,
    corrected_duration: t.durationSeconds ?? 0,
    answered_by: t.answeredBy,
    call_analysis: {
      outcome: t.outcome ?? '',
      summary: t.summary,
      timeline,
      answers,
    },
    concatenated_transcript: t.transcript,
    recordingUrl: t.recordingUrl,
    isTest: t.isTest ?? null,
  };
}

interface Column {
  header: ReactNode;
  key: string;
  cell?: (item: Record<string, unknown>) => ReactNode;
  width?: string;
  isCentered?: boolean;
  isPinned?: boolean;
  pinPosition?: string;
  sortable?: boolean;
}

interface LiveTransfersModalProps {
  isOpen: boolean;
  onClose: () => void;
  dateRange: {
    from: Date | null;
    to: Date | null;
  };
  orgTimezone?: string;
  transferCount?: number;
  transfers?: GQLLiveTransferCall[];
  total?: number;
  hasMore?: boolean;
  isLoading?: boolean;
  loadMore?: () => void;
  loadPage?: (offset: number, limit: number) => void;
  /** Updates the parent's useOrgLiveTransfersGQL filter state (search/flow/dateRange). */
  gqlFilter?: (params: {
    search?: string;
    answeredBy?: string;
    flowName?: string;
    dateFrom?: string;
    dateTo?: string;
  }) => void;
  onContactClick?: (contactId: string) => void;
}

export const LiveTransfersModal: FC<LiveTransfersModalProps> = ({
  isOpen,
  onClose,
  dateRange,
  orgTimezone,
  transfers: providedTransfers,
  total: providedTotal,
  isLoading: providedIsLoading,
  loadPage: providedLoadPage,
  gqlFilter: providedGqlFilter,
  onContactClick,
}) => {
  const PAGE_SIZE_DEFAULT = 25;
  const [currentPage, setCurrentPage] = useState(0);
  const [pageSize, setPageSize] = useState(PAGE_SIZE_DEFAULT);
  const [selectedTransfer, setSelectedTransfer] = useState<GQLLiveTransferCall | null>(null);
  const [showInfoOnMobile, setShowInfoOnMobile] = useState(false);

  // Filter state — search + date range. Flow filter is hidden until backend
  // supports filtering by flowId (name-based filtering collides when two
  // flows share a name across types). Answered-by is omitted because every
  // live transfer is human-answered by definition.
  const [searchQuery, setSearchQuery] = useState('');
  // Storing the date-range override (or null when "follow parent") lets us
  // skip the prop→state sync useEffect: the active range is derived inline.
  const [dateRangeOverride, setDateRangeOverride] = useState<
    { from: Date | null; to: Date | null } | null
  >(null);
  const activeDateRange = dateRangeOverride ?? dateRange;
  const shouldLoadTransfers = !providedTransfers;
  const fallbackTransfers = useOrgLiveTransfersGQL({
    dateRange: activeDateRange,
    enabled: isOpen && shouldLoadTransfers,
    pageSize,
  });

  const transfers = providedTransfers ?? fallbackTransfers.transfers;
  const total = providedTotal ?? fallbackTransfers.total;
  const isLoading = providedIsLoading ?? fallbackTransfers.isLoading;
  const loadPage = providedLoadPage ?? fallbackTransfers.loadPage;
  const gqlFilter = providedGqlFilter ?? fallbackTransfers.filter;


  // Event-driven debounce — no useEffect required. Each onChange builds
  // the latest filter params from its event value + current state closure
  // and queues a single backend call 400ms later. Subsequent calls cancel
  // the prior timer so only the last change wins.
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const scheduleFilter = (next: {
    search?: string;
    dateRange?: { from: Date | null; to: Date | null };
  }) => {
    if (!gqlFilter) return;
    const search = next.search ?? searchQuery;
    const range = next.dateRange ?? activeDateRange;
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => {
      gqlFilter({
        search: search || undefined,
        dateFrom: range.from?.toISOString() ?? undefined,
        dateTo: range.to?.toISOString() ?? undefined,
      });
      setCurrentPage(0);
    }, 400);
  };

  const handleSearchChange = (val: string) => {
    setSearchQuery(val);
    scheduleFilter({ search: val });
  };

  const handleDateRangeChange = (range: { from: Date | null; to: Date | null }) => {
    setDateRangeOverride(range);
    scheduleFilter({ dateRange: range });
  };

  const handleResetFilters = () => {
    setSearchQuery('');
    setDateRangeOverride(null);
    scheduleFilter({ search: '', dateRange });
  };

  // Used as a fallback when the call has only assignedAgentId without the
  // nested assignedAgent object (lazy resolver miss / stale row). Match the
  // contact-table pattern: prefer the snapshot, otherwise look the agent up
  // from the org users list.
  // Pull a large page so the assignedAgentId fallback lookup doesn't miss
  // agents who would sit beyond the default 200-row cap on bigger orgs.
  const { users: orgUsers } = useOrganizationUsers({ enabled: isOpen });

  const columns: Column[] = [
    {
      header: 'Name',
      key: 'contactName',
      width: '200px',
      isPinned: true,
      pinPosition: 'left',
      cell: (item) => {
        const t = item as unknown as GQLLiveTransferCall;
        const displayName = t.contactName || formatPhoneNumber(t.toNumber) || '-';
        return (
          <div className="flex items-center gap-3 min-w-0">
            {t.inbound ? (
              <PhoneIncoming className="h-3.5 w-3.5 text-blue-500 shrink-0" />
            ) : (
              <PhoneOutgoing className="h-3.5 w-3.5 text-emerald-500 shrink-0" />
            )}
            {onContactClick && t.contactId ? (
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  onContactClick(t.contactId!);
                }}
                className="font-medium text-left truncate min-w-0 text-primaryfortext underline underline-offset-2 hover:text-primary/80"
                title={displayName}
              >
                {displayName}
              </button>
            ) : (
              <span className="font-medium text-left truncate min-w-0" title={displayName}>{displayName}</span>
            )}
          </div>
        );
      },
    },
    {
      header: 'Phone',
      key: 'toNumber',
      width: '150px',
      cell: (item) => (
        <span className="text-foreground">
          {formatPhoneNumber((item as unknown as GQLLiveTransferCall).toNumber) || '-'}
        </span>
      ),
    },
    {
      header: 'Date/Time',
      key: 'initiatedAt',
      width: '180px',
      cell: (item) => {
        const t = item as unknown as GQLLiveTransferCall;
        const ts = t.transfer?.createdAt || t.transfer?.initiatedAt || t.initiatedAt;
        return ts ? (
          <span className="whitespace-nowrap text-foreground">{formatDate(ts, orgTimezone, 'MMM D, h:mm A')}</span>
        ) : (
          <span className="text-muted-foreground">-</span>
        );
      },
    },
    {
      header: 'Answered',
      key: 'answered',
      width: '120px',
      cell: (item) => {
        const t = (item as unknown as GQLLiveTransferCall).transfer;
        const answered = deriveAnswered(t?.answered, t?.status);
        if (answered === true) {
          return (
            <Badge className="inline-flex items-center gap-1.5 bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-300">
              <CheckCircle className="w-3 h-3" />
              <span>Yes</span>
            </Badge>
          );
        }
        if (answered === false) {
          return (
            <Badge className="inline-flex items-center gap-1.5 bg-muted text-muted-foreground">
              <XCircle className="w-3 h-3" />
              <span>No</span>
            </Badge>
          );
        }
        return <span className="text-muted-foreground">-</span>;
      },
    },
    {
      header: 'Accepted By',
      key: 'acceptedBy',
      width: '180px',
      cell: (item) => {
        const t = (item as unknown as GQLLiveTransferCall).transfer;
        if (deriveAnswered(t?.answered, t?.status) !== true) {
          return <span className="text-muted-foreground">-</span>;
        }
        const who = t?.acceptedBy
          || (t?.targetAgentNumber ? formatPhoneNumber(t.targetAgentNumber) : null);
        return (
          <span className="text-foreground truncate max-w-[160px] inline-block align-middle">
            {who || '-'}
          </span>
        );
      },
    },
    {
      header: 'Assigned To',
      key: 'assignedAgent',
      width: '220px',
      cell: (item) => {
        const c = item as unknown as GQLLiveTransferCall;
        return (
          <AssignedAgentCell
            agent={(c.assignedAgent ?? null) as AssignedAgent | null}
            agentId={c.assignedAgentId ?? null}
            orgUsers={orgUsers}
          />
        );
      },
    },
    {
      header: 'Reason',
      key: 'transferReason',
      width: '300px',
      cell: (item) => {
        const reason = (item as unknown as GQLLiveTransferCall).transfer?.transferReason;
        return <ExpandableTextCell text={reason} />;
      },
    },
  ];

  const TransferDetailsPanel: FC<{ transfer: GQLLiveTransferCall }> = ({ transfer }) => {
    const ltAnswered = transfer.transfer?.answered;
    const [isAssocCallOpen, setIsAssocCallOpen] = useState(true);
    const adaptedCall = adaptTransferToCall(transfer);
    const flowInfo = transfer.flowName
      ? { name: transfer.flowName, type: transfer.flowType || '-' }
      : undefined;

    return (
      <div className="h-full flex flex-col overflow-y-auto scrollbar-custom">
        <div className="flex flex-col">
          {/* Contact */}
          <div className="px-5 py-5 border-b border-border">
            {onContactClick && transfer.contactId ? (
              <button
                onClick={() => onContactClick(transfer.contactId!)}
                className="t-name truncate text-left text-primaryfortext underline underline-offset-2 hover:text-primary/80"
              >
                {transfer.contactName || formatPhoneNumber(transfer.toNumber) || '-'}
              </button>
            ) : (
              <p className="t-name truncate">
                {transfer.contactName || formatPhoneNumber(transfer.toNumber) || '-'}
              </p>
            )}
            {transfer.toNumber && (
              <p className="t-label mt-0.5">{formatPhoneNumber(transfer.toNumber)}</p>
            )}
          </div>

          <div className="px-5 py-5 space-y-3.5">
            <span className="t-heading">Transfer Details</span>
            {ltAnswered === true && (
              <div className="flex items-center gap-2 min-w-0">
                <CheckCircle className="t-icon-sm shrink-0 text-green-600 dark:text-green-400" />
                <span className="t-label shrink-0">Answered by:</span>
                <span className="t-body truncate">
                  {transfer.transfer?.acceptedBy
                    ? transfer.transfer.acceptedBy
                    : transfer.transfer?.targetAgentNumber
                      ? formatPhoneNumber(transfer.transfer.targetAgentNumber)
                      : '—'}
                </span>
              </div>
            )}
            {ltAnswered === false && (
              <div className="flex items-center gap-2 min-w-0">
                <XCircle className="t-icon-sm shrink-0 text-muted-foreground" />
                <span className="t-label shrink-0">Answered:</span>
                <span className="t-body">No answer</span>
              </div>
            )}
            {transfer.transfer?.failureReason && (
              <div className="flex items-start gap-2 min-w-0">
                <XCircle className="t-icon-sm shrink-0 text-red-500 mt-0.5" />
                <span className="t-label shrink-0">Failure:</span>
                <span className="t-body">{formatTransferReason(transfer.transfer.failureReason)}</span>
              </div>
            )}
            {(transfer.transfer?.recordingUrl || transfer.transfer?.voicemailUrl) &&
              transfer.transfer?.transferId && (
                <div className="flex flex-col gap-3">
                  {transfer.transfer?.recordingUrl && (
                    <div className="flex items-center gap-2 min-w-0">
                      <Phone className="t-icon-sm shrink-0 text-primary" />
                      <span className="t-label shrink-0">Transfer:</span>
                      <CallRecordingPlayer
                        transferId={transfer.transfer.transferId}
                        kind="recording"
                        hasRecording={!!transfer.transfer.recordingUrl}
                      />
                    </div>
                  )}
                  {transfer.transfer?.voicemailUrl && (
                    <div className="flex items-center gap-2 min-w-0">
                      <Voicemail className="t-icon-sm shrink-0 text-primary" />
                      <span className="t-label shrink-0">Voicemail:</span>
                      <CallRecordingPlayer
                        transferId={transfer.transfer.transferId}
                        kind="voicemail"
                        hasRecording={!!transfer.transfer.voicemailUrl}
                      />
                    </div>
                  )}
                </div>
              )}
            {transfer.transfer?.transferReason && (
              <div className="space-y-2">
                <span className="t-heading">Reason for transfer</span>
                <div className="bg-green-50 dark:bg-green-900/20 p-4 rounded-lg border border-[#A8F3D0] dark:border-green-700/30">
                  <p className="text-gray-700 dark:text-gray-300 text-sm whitespace-pre-wrap">
                    {transfer.transfer.transferReason}
                  </p>
                </div>
              </div>
            )}
          </div>

          {/* Associated Call — only when the transfer actually linked to a
              Calls row. LiveTransfers-primary rows (SPEC-2172) can have no
              originating call (null original_call_id), so there's nothing to
              show. */}
          {transfer.hasLinkedCall && (
            <div className="border-y-2 border-primary/40">
              <button
                type="button"
                onClick={() => setIsAssocCallOpen((o) => !o)}
                aria-expanded={isAssocCallOpen}
                className="w-full px-5 py-4 bg-primary/20 hover:bg-primary/25 dark:bg-primary/15 dark:hover:bg-primary/20 transition-colors flex items-center justify-between text-left border-l-4 border-primary"
              >
                <div>
                  <p className="text-sm font-bold uppercase tracking-wide text-foreground">
                    Associated Call
                  </p>
                  <p className="text-xs text-muted-foreground">
                    The call that initiated this transfer
                  </p>
                </div>
                {isAssocCallOpen ? (
                  <ChevronUp className="h-5 w-5 text-primary" />
                ) : (
                  <ChevronDown className="h-5 w-5 text-primary" />
                )}
              </button>
              {isAssocCallOpen && (
                <CallDetailsPanel
                  call={adaptedCall}
                  orgTimezone={orgTimezone}
                  flowInfo={flowInfo}
                  contactFirstName={transfer.contactName?.split(' ')[0]}
                />
              )}
            </div>
          )}
        </div>
      </div>
    );
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} size="full" className="mx-0 h-full">
      <Modal.Header>
        <div className="flex flex-col gap-0.5">
          <div className="flex flex-col sm:flex-row sm:items-center gap-1 sm:gap-3">
            <Typography variant="h2">Live Transfers</Typography>
            <div className="flex items-center gap-1.5 text-xs text-muted-foreground font-normal">
              <Calendar className="h-3.5 w-3.5" />
              <span>
                {dateRange.from && dateRange.to
                  ? `${formatDate(dateRange.from, orgTimezone, 'MMM D, YYYY')} - ${formatDate(dateRange.to, orgTimezone, 'MMM D, YYYY')}`
                  : 'All Transfers'}
              </span>
            </div>
          </div>
          <div className="text-xs text-muted-foreground">
            {formatNumber(total)} {total === 1 ? 'record' : 'records'}
          </div>
        </div>
      </Modal.Header>
      <Modal.Body className="flex-1 flex flex-col min-h-0">
        <div className="flex flex-1 min-h-0 gap-4 overflow-hidden md:flex-row flex-col">
          <div className={`md:w-[65%] w-full flex-1 md:flex-none flex flex-col min-h-0 ${showInfoOnMobile ? 'hidden md:flex' : ''}`}>
            <div className="flex w-full mb-4 shrink-0">
              <CallStatsHeader
                onSearch={handleSearchChange}
                onDateRangeChange={handleDateRangeChange}
                onReset={handleResetFilters}
                searchQuery={searchQuery}
                dateRange={activeDateRange}
                flowOptions={[]}
                hideAnsweredBy
                hideFlowFilter
              />
            </div>
            <div
              className={cn(
                STAT_CELL_TEXT,
                'flex flex-col flex-1 min-h-0 border border-foreground/30 rounded-lg overflow-hidden bg-white dark:bg-transparent [&_tbody_tr]:cursor-pointer',
              )}
            >
              <div className="flex-1 min-h-0">
                <Table
                  data={transfers as unknown as Record<string, unknown>[]}
                  columns={columns}
                  keyExtractor={(item) => (item as unknown as GQLLiveTransferCall).callId}
                  loading={isLoading}
                  size="xl"
                  rowSelectable
                  selectedRowId={selectedTransfer?.callId}
                  onRowSelect={(id) => {
                    const t = transfers.find((tr) => tr.callId === id);
                    if (t) {
                      setSelectedTransfer(t);
                      setShowInfoOnMobile(true);
                    }
                  }}
                  className="w-full"
                />
              </div>
              <div className="border-t border-foreground/30 bg-muted/30 dark:bg-muted/20">
                <OffsetPagination
                  currentPage={currentPage}
                  totalItems={total}
                  pageSize={pageSize}
                  isLoading={isLoading}
                  label="transfers"
                  onNextPage={() => {
                    const next = currentPage + 1;
                    setCurrentPage(next);
                    loadPage?.(next * pageSize, pageSize);
                  }}
                  onPrevPage={() => {
                    const prev = Math.max(0, currentPage - 1);
                    setCurrentPage(prev);
                    loadPage?.(prev * pageSize, pageSize);
                  }}
                  onPageChange={(page) => {
                    setCurrentPage(page);
                    loadPage?.(page * pageSize, pageSize);
                  }}
                  onPageSizeChange={(size) => {
                    setPageSize(size);
                    setCurrentPage(0);
                    loadPage?.(0, size);
                  }}
                />
              </div>
            </div>
          </div>
          <div
            className={`md:w-[35%] w-full md:h-full min-h-0 max-h-[60vh] md:max-h-none rounded-xl border border-border bg-muted/30 overflow-y-auto scrollbar-custom ${
              !showInfoOnMobile && !selectedTransfer ? 'hidden md:block' : ''
            }`}
          >
            {showInfoOnMobile && (
              <div className="p-4 border-b border-border md:hidden">
                <BackButton
                  onClick={() => {
                    setShowInfoOnMobile(false);
                    setSelectedTransfer(null);
                  }}
                />
              </div>
            )}
            {selectedTransfer ? (
              <TransferDetailsPanel transfer={selectedTransfer} />
            ) : (
              <div className="h-full flex flex-col items-center justify-center text-center px-6">
                <div className="w-12 h-12 rounded-full bg-muted flex items-center justify-center mb-3">
                  <Phone className="w-5 h-5 text-muted-foreground" />
                </div>
                <p className="text-sm font-medium text-muted-foreground">
                  Select a transfer to view details
                </p>
                <p className="text-xs text-muted-foreground mt-1">
                  Click any row in the table to see the full transfer breakdown
                </p>
              </div>
            )}
          </div>
        </div>
      </Modal.Body>
    </Modal>
  );
};
